import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api, FieldConfig, MappingsResponse } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw } from 'lucide-react';

interface FormFieldMappingProps {
  formId: string;
}

type TemplateType = 'default' | 'compact' | 'detailed' | 'custom';
type FormatType = 'plain' | 'link' | 'bold' | 'code' | 'hidden';

const templates: { value: TemplateType; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'compact', label: 'Compact' },
  { value: 'detailed', label: 'Detailed' },
  { value: 'custom', label: 'Custom' },
];

const formatOptions: { value: FormatType; label: string }[] = [
  { value: 'plain', label: 'Plain' },
  { value: 'link', label: 'Link' },
  { value: 'bold', label: 'Bold' },
  { value: 'code', label: 'Code' },
  { value: 'hidden', label: 'Hidden' },
];

const defaultSampleData: Record<string, string> = {
  name: 'John Doe',
  email: 'john@example.com',
  message: 'This is a test message',
};

export function FormFieldMapping({ formId }: FormFieldMappingProps) {
  const { toast } = useToast();

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // Data states
  const [template, setTemplate] = useState<TemplateType>('default');
  const [messageFormat] = useState('markdown');
  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [customTemplate, setCustomTemplate] = useState<string>('');

  // Preview states
  const [sampleData, setSampleData] = useState<Record<string, string>>(defaultSampleData);
  const [previewText, setPreviewText] = useState<string>('');

  // UI states
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMappingConfig();
  }, [formId]);

  const loadMappingConfig = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await api.getFormMappings(formId) as MappingsResponse;

      setTemplate(data.template as TemplateType);

      if (data.config.fields && data.config.fields.length > 0) {
        setFields(data.config.fields);
        // Update sample data keys from fields
        const newSampleData: Record<string, string> = {};
        data.config.fields.forEach((field) => {
          newSampleData[field.source_field] = defaultSampleData[field.source_field] || `Sample ${field.label}`;
        });
        setSampleData(newSampleData);
      } else {
        // Use default fields if none exist
        setFields([
          { source_field: 'name', label: 'Name', show_in_message: true, format: 'plain' },
          { source_field: 'email', label: 'Email', show_in_message: true, format: 'link' },
          { source_field: 'message', label: 'Message', show_in_message: true, format: 'plain' },
        ]);
      }

      if (data.config.custom_template) {
        setCustomTemplate(data.config.custom_template);
      }

      // Load initial preview
      await generatePreview(data.template as TemplateType, data.config.fields || []);
    } catch (err) {
      setError('Failed to load mapping configuration');
      console.error('Mapping config error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const generatePreview = async (
    templateOverride?: TemplateType,
    fieldsOverride?: FieldConfig[]
  ) => {
    setIsPreviewLoading(true);

    try {
      const result = await api.previewFormMappings(formId, {
        template: templateOverride || template,
        message_format: messageFormat,
        config: {
          fields: fieldsOverride || fields,
          custom_template: (templateOverride || template) === 'custom' ? customTemplate : undefined,
        },
        sample_data: sampleData,
      });

      setPreviewText(result.preview);
    } catch (err) {
      console.error('Preview error:', err);
      setPreviewText('Failed to generate preview');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleTemplateChange = (newTemplate: TemplateType) => {
    setTemplate(newTemplate);
    setHasChanges(true);
    generatePreview(newTemplate);
  };

  const handleFieldChange = (index: number, updated: Partial<FieldConfig>) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updated };
    setFields(newFields);
    setHasChanges(true);
  };

  const handleCustomTemplateChange = (value: string) => {
    setCustomTemplate(value);
    setHasChanges(true);
  };

  const handleSampleDataChange = (key: string, value: string) => {
    setSampleData({ ...sampleData, [key]: value });
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await api.updateFormMappings(formId, {
        template,
        message_format: messageFormat,
        config: {
          fields,
          custom_template: template === 'custom' ? customTemplate : undefined,
        },
      });

      setHasChanges(false);
      toast({
        title: 'Success',
        description: 'Message formatting saved successfully',
      });
    } catch (err) {
      console.error('Save error:', err);
      toast({
        title: 'Error',
        description: 'Failed to save configuration',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Message Formatting</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-gray-500">
            Loading configuration...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Message Formatting</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-gray-500">
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Message Formatting</CardTitle>
            <CardDescription>Configure how form submissions appear in Telegram</CardDescription>
          </div>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Template Selector */}
        <div className="space-y-2">
          <Label>Template</Label>
          <div className="flex gap-1">
            {templates.map((t) => (
              <Button
                key={t.value}
                variant={template === t.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleTemplateChange(t.value)}
              >
                {t.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Custom Template Editor */}
        {template === 'custom' && (
          <div className="space-y-2">
            <Label>Custom Template</Label>
            <Textarea
              value={customTemplate}
              onChange={(e) => handleCustomTemplateChange(e.target.value)}
              placeholder="Enter custom template with {{field_name}} placeholders"
              rows={4}
              className="font-mono text-sm"
            />
            <p className="text-sm text-gray-500">
              Available placeholders: {fields.map(f => `{{${f.source_field}}}`).join(', ')}
            </p>
          </div>
        )}

        {/* Field Configuration */}
        <div className="space-y-3">
          <Label>Fields</Label>
          <div className="border rounded-lg overflow-hidden">
            <div className="grid grid-cols-[1fr_1fr_120px_60px] gap-4 p-3 bg-gray-50 text-sm font-medium text-gray-700 border-b">
              <span>Field</span>
              <span>Label</span>
              <span>Format</span>
              <span>Show</span>
            </div>
            <div className="divide-y">
              {fields.map((field, index) => (
                <div
                  key={field.source_field}
                  className="grid grid-cols-[1fr_1fr_120px_60px] gap-4 p-3 items-center"
                >
                  <span className="text-sm font-mono text-gray-600">
                    {field.source_field}
                  </span>
                  <Input
                    value={field.label}
                    onChange={(e) =>
                      handleFieldChange(index, { label: e.target.value })
                    }
                    className="h-8"
                  />
                  <Select
                    value={field.format}
                    onValueChange={(value: FormatType) =>
                      handleFieldChange(index, { format: value })
                    }
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {formatOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex justify-center">
                    <Switch
                      checked={field.show_in_message}
                      onCheckedChange={(checked) =>
                        handleFieldChange(index, { show_in_message: checked })
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Preview</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => generatePreview()}
              disabled={isPreviewLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isPreviewLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Sample Data */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(sampleData).map(([key, value]) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs text-gray-500">{key}</Label>
                <Input
                  value={value}
                  onChange={(e) => handleSampleDataChange(key, e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            ))}
          </div>

          {/* Preview Result */}
          <div className="border rounded-lg p-4 bg-gray-50 min-h-[120px]">
            {isPreviewLoading ? (
              <div className="flex items-center justify-center h-20 text-gray-500">
                Generating preview...
              </div>
            ) : (
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
                {previewText || 'Click "Refresh" to generate preview'}
              </pre>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
